import { Controller } from "@hotwired/stimulus"
import { motionMs } from "../lib/motion"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { NativeBridge } from "../lib/native_bridge"
import { whenTabShown } from "../lib/overlay"

/**
 * Location Verification Modal Controller (Stimulus)
 * 30일 경과 사용자에게 위치 재인증을 유도하는 Modal 관리
 *
 * Features:
 * - Auto-show modal on page load if needs reverification
 * - GPS permission request and location update
 * - AJAX communication with UsersController#update_location
 * - Success/error status display
 * - Session storage for "skip" action (1-day delay)
 *
 * Targets:
 * - status: Status message container
 * - statusMessage: Status text
 * - verifyButton: "Verify Now" button
 * - verifyButtonText: Button text (changes to "Verifying...")
 *
 * Actions:
 * - verifyNow: Request GPS and update location
 * - skip: Close modal and set skip flag for 1 day
 *
 * Values:
 * - userId: Current user ID
 */
const log = createLogger('LocationVerification')

export default class extends Controller {
  static targets = ["overlay", "modalContent", "status", "statusMessage", "verifyButton", "verifyButtonText"]
  static values = {
    userId: Number,
    context: String,
    autoShow: { type: Boolean, default: true }
  }

  /**
   * Initialize controller
   * Check skip flag first, then show modal with animation if needed
   */
  connect() {
    // Pre-load i18n messages (called once on connect)
    this.messages = {
      success: getI18nMessage(this.element, 'statusSuccess', '位置驗證成功！'),
      error: getI18nMessage(this.element, 'statusError', '位置驗證失敗，請稍後再試'),
      permission_denied: getI18nMessage(this.element, 'errorsPermissionDenied', '需要位置權限才能繼續使用'),
      unavailable: getI18nMessage(this.element, 'errorsUnavailable', '無法取得位置資訊'),
      timeout: getI18nMessage(this.element, 'errorsTimeout', '位置請求逾時'),
      chat_skip_disabled: getI18nMessage(this.element, 'errorsChatSkipDisabled', '聊天功能需要驗證位置，無法跳過'),
      verify_now: getI18nMessage(this.element, 'actionsVerifyNow', '立即驗證'),
      verifying: getI18nMessage(this.element, 'actionsVerifying', '驗證中...')
    }

    // Auto-show mode: check skip flag and show modal with animation
    // Manual mode (auto_show=false): skip flag irrelevant, modal controlled by parent
    if (this.autoShowValue !== false) {
      const skipUntil = sessionStorage.getItem("locationVerificationSkipUntil")
      if (skipUntil && new Date(skipUntil) > new Date()) {
        // Skip valid - remove from DOM immediately (already invisible with opacity-0)
        const modalElement = this.hasOverlayTarget ? this.overlayTarget : this.element
        modalElement.remove()
        return
      }

      // Show modal with fade-in animation (from opacity-0 to opacity-1), once this tab is on screen
      this.cancelAutoShow = whenTabShown(() => this.showWithAnimation())
    }

    log.debug('Location verification modal initialized')
  }

  disconnect() {
    this.cancelAutoShow?.()
  }

  /**
   * Show modal with fade-in animation
   * Used for auto_show mode where modal starts with opacity-0
   */
  showWithAnimation() {
    const modalElement = this.hasOverlayTarget ? this.overlayTarget : this.element
    const animationDuration = motionMs("BASE_MS")

    // Use requestAnimationFrame to ensure CSS transition works
    requestAnimationFrame(() => {
      modalElement.style.transition = `opacity ${animationDuration}ms var(--ease-decelerate)`
      modalElement.style.opacity = "1"
    })
  }

  /**
   * Verify Now - Request GPS permission and update location
   * @param {Event} event Click event
   */
  async verifyNow(event) {
    event.preventDefault()

    // Disable button and show loading state
    this.setLoading(true)

    try {
      // Request GPS permission (high accuracy)
      const position = await this.requestGPSPermission()

      // Validate coordinates are within Taiwan boundaries (client-side UX)
      const { latitude, longitude } = position.coords
      if (!this.isWithinTaiwan(latitude, longitude)) {
        const outsideTaiwanMessage = getI18nMessage(
          this.element,
          'errorsOutsideTaiwan',
          '您不在台灣境內，無法使用此服務（包含金門、馬祖、澎湖等離島）'
        )
        this.showStatus("error", outsideTaiwanMessage)
        return
      }

      // Update location via AJAX
      await this.updateLocation(latitude, longitude)

      // Show success message
      this.showStatus("success", this.messages.success)
      this.dispatch("verified")

      // Store refresh callback in closure BEFORE element removal
      const performRefresh = () => {
        Turbo.visit(window.location.href, { action: "replace" })
      }

      // Close modal and schedule refresh
      setTimeout(() => {
        this.closeModal()

        // Use window.setTimeout to survive controller disconnect
        window.setTimeout(
          performRefresh,
          window.BusinessConstants.UITiming.ANIMATION_DURATION_MS
        )
      }, window.BusinessConstants.UITiming.MODAL_AUTO_CLOSE_MS)

    } catch (error) {
      // Handle GPS permission errors
      this.handleGPSError(error)
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Show modal (for manual trigger)
   * Used when "重新認證" button is clicked
   */
  show() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
      this.overlayTarget.style.opacity = "1"
      log.debug('Location verification modal shown')
    }
  }

  /**
   * Handle backdrop click - close modal
   * stopPropagation on modalContent ensures only backdrop clicks reach here
   * @param {Event} event Click event
   */
  handleBackdropClick(event) {
    this.skip(event)
  }

  /**
   * Handle escape key - close modal
   * Supports both auto_show and manual trigger modes
   * @param {Event} event Keyboard event
   */
  handleEscapeKey(event) {
    const modalElement = this.hasOverlayTarget ? this.overlayTarget : this.element
    if (!modalElement.classList.contains('hidden')) {
      this.skip(event)
    }
  }

  /**
   * Stop event propagation (for modal content)
   * @param {Event} event Click event
   */
  stopPropagation(event) {
    event.stopPropagation()
  }

  /**
   * Skip - Close modal and set skip flag for 24 hours
   * 채팅 컨텍스트에서는 skip 불가
   * @param {Event} event Click event
   */
  skip(event) {
    event.preventDefault()

    // 채팅 컨텍스트에서는 skip 불가
    if (this.contextValue === 'chat') {
      this.showStatus('error', this.messages.chat_skip_disabled)
      return
    }

    // Set skip flag for 24 hours
    const skipUntil = new Date()
    skipUntil.setHours(skipUntil.getHours() + 24)
    sessionStorage.setItem("locationVerificationSkipUntil", skipUntil.toISOString())

    // Close modal
    this.closeModal()
  }

  /**
   * Request GPS permission from browser or native app
   * Uses NativeBridge.GPS for Android/iOS, navigator.geolocation for web
   * @returns {Promise<GeolocationPosition>}
   * @throws {GeolocationPositionError}
   */
  async requestGPSPermission() {
    // Native app: use NativeBridge.GPS for proper permission dialogs
    if (NativeBridge.isNative()) {
      log.info('Using NativeBridge.GPS for location request')
      try {
        const location = await NativeBridge.GPS.requestLocation({ accuracy: "high" })

        // NaN 좌표 방어 체크 (product-form/location.js 패턴)
        if (Number.isNaN(location.latitude) || Number.isNaN(location.longitude)) {
          throw new Error("Invalid coordinates received from GPS")
        }

        // Return in GeolocationPosition-compatible format
        return {
          coords: {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy
          }
        }
      } catch (error) {
        // Native 에러를 GeolocationPositionError 형식으로 변환
        throw this.convertNativeError(error)
      }
    }

    // Web browser: use navigator.geolocation
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: window.BusinessConstants.UITiming.GPS_TIMEOUT_MS,
          maximumAge: 0
        }
      )
    })
  }

  /**
   * Convert NativeBridge error to GeolocationPositionError-compatible format
   * @param {Error} error NativeBridge GPS error
   * @returns {Object} GeolocationPositionError-like object with code property
   */
  convertNativeError(error) {
    const isPermissionDenied = error.message?.includes('PERMISSION_DENIED')
    const isGpsDisabled = error.message?.includes('GPS_DISABLED')

    // GeolocationPositionError 코드 매핑
    // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
    let errorCode
    if (isPermissionDenied) {
      errorCode = 1 // PERMISSION_DENIED
    } else if (isGpsDisabled) {
      errorCode = 2 // POSITION_UNAVAILABLE (GPS hardware disabled)
    } else {
      errorCode = 2 // POSITION_UNAVAILABLE (default for native errors)
    }

    return {
      code: errorCode,
      message: error.message || 'Native GPS error'
    }
  }

  /**
   * Update location via AJAX (UsersController#update_location)
   * @param {number} latitude User latitude
   * @param {number} longitude User longitude
   * @returns {Promise<void>}
   */
  async updateLocation(latitude, longitude) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch("/update_location", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ latitude, longitude })
    })

    const data = await response.json()

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Location update failed")
    }

    log.info('Location updated successfully', { district: data.district })
  }

  /**
   * Handle GPS permission errors
   * @param {GeolocationPositionError|Error} error
   */
  handleGPSError(error) {
    let messageKey = "error"

    if (error.code) {
      switch (error.code) {
        case 1: // PERMISSION_DENIED
          messageKey = "permission_denied"
          break
        case 2: // POSITION_UNAVAILABLE
          messageKey = "unavailable"
          break
        case 3: // TIMEOUT
          messageKey = "timeout"
          break
      }
    }

    this.showStatus("error", this.messages[messageKey])
    log.error('GPS error', error)
  }

  /**
   * Show status message
   * @param {string} type "success" or "error"
   * @param {string} message Status message
   */
  showStatus(type, message) {
    if (!this.hasStatusTarget) return

    const statusEl = this.statusTarget
    const messageEl = this.statusMessageTarget

    // Set message
    messageEl.textContent = message

    // Set styles based on type
    if (type === "success") {
      statusEl.className = "rounded-lg p-3 text-sm bg-green-50 text-green-700 border border-green-200"
    } else {
      statusEl.className = "rounded-lg p-3 text-sm bg-red-50 text-red-700 border border-red-200"
    }

    // Show status
    statusEl.classList.remove("hidden")
  }

  /**
   * Set loading state
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (!this.hasVerifyButtonTarget) return

    const button = this.verifyButtonTarget
    const buttonText = this.verifyButtonTextTarget

    if (isLoading) {
      button.disabled = true
      buttonText.textContent = this.messages.verifying
    } else {
      button.disabled = false
      buttonText.textContent = this.messages.verify_now
    }
  }

  /**
   * Close modal with fade-out animation
   */
  closeModal() {
    const animationDuration = motionMs("QUICK_MS")

    // Use overlay target if available (manual trigger mode), otherwise use element (auto-show mode)
    const modalElement = this.hasOverlayTarget ? this.overlayTarget : this.element

    // Fade out
    modalElement.style.opacity = "0"
    modalElement.style.transition = `opacity ${animationDuration}ms var(--ease-exit)`

    // For manual trigger mode, just hide; for auto-show mode, remove from DOM
    setTimeout(() => {
      if (this.hasOverlayTarget && this.autoShowValue === false) {
        // Manual trigger mode - just hide, don't remove
        modalElement.classList.add('hidden')
        modalElement.style.opacity = ""
        modalElement.style.transition = ""
      } else {
        // Auto-show mode - remove from DOM
        modalElement.remove()
      }
    }, animationDuration)
  }

  /**
   * Check if coordinates are within Taiwan boundaries
   * Uses BusinessConstants.GPS for SSOT
   * @param {number} lat Latitude
   * @param {number} lng Longitude
   * @returns {boolean} true if within Taiwan
   */
  isWithinTaiwan(lat, lng) {
    const { TAIWAN_MIN_LAT, TAIWAN_MAX_LAT, TAIWAN_MIN_LNG, TAIWAN_MAX_LNG } = window.BusinessConstants.GPS

    return lat >= TAIWAN_MIN_LAT && lat <= TAIWAN_MAX_LAT &&
           lng >= TAIWAN_MIN_LNG && lng <= TAIWAN_MAX_LNG
  }
}
